import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';
import { ApplicationState } from '../../../store';
import * as Models from '../../../store/Models/Surveys/SurveyDataModel';
import * as SurveyStateController from '../../../store/StateControllers/Survey/SurveyStateController';
import SelectSuppliersMaterialsTable, { SelectSuppliersMaterialsTableRenderOptions } from '../../core/SelectSuppliersMaterialsTable';
import { SurveyProps } from '../CreateAndEditSurveys';
import { CreateAndEditSurveyComponentPaths } from '../SurveyComponentPaths';
import "./../styles.css";
import * as SelectSuppliersStateControllers from "../../../store/StateControllers/Supplier/SelectSuppliersMaterialsTableStateController";
import { InviteSupplierModel } from '../../../store/Models/Supplier/Supplier';
import { InventoryOrCustomNameWithPossibleId } from '../../../store/Models/InventoryNameWithPossibleId';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { TeamRole } from '../../../store/Models/UserContext';
import CheckBox from '../../core/CheckBox';

type Props = SurveyProps & typeof mapDispatchToProps;

class CreateAndEditSurveySummaryPage extends ToxnotSignInRequiredComponent<Props>{

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }

    // ===============================================================================

    private shouldDisplaySupplierAndMaterialData = () =>
        this.props.surveyRequest.bomType !== 2 &&
        this.props.surveyUi.currentState !== Models.SurveyMode.Template;

    // ===============================================================================

    private shouldDisplayCampaignSection = () =>
        this.props.surveyUi.currentState !== Models.SurveyMode.Template &&
        (this.props.appServices.userContext?.isSubscriptionActive ?? false);

    // ===============================================================================

    protected renderConcreteComponent() {

        const tableProps: SelectSuppliersMaterialsTableRenderOptions = {
            supplierOnlyMode: this.props.surveyRequest.bomType === 2,
            includeActionButtons: false,
            readOnlyMode: !this.isSurveyEditable(),
            allowAddNewMaterials: this.props.surveyRequest.state !== 4 /* 4 = draft */,
            lockSuppliers: this.props.surveyRequest.state === 4 && this.props.surveyUi.currentState === Models.SurveyMode.EditExisting,
            takeFullHeightOfParent: false
        }

        return (
            <div className="tn-section-row">
                <div className="tn-section-col-12">
                    <div className="grouped-data">
                        {this.props.surveyUi.currentState !== Models.SurveyMode.Template && <div className="group">
                            <div className="title">{this.localizeString("Survey Name")}</div>
                            <div className="content">
                                <div className="form-inline">
                                    <input type="text" value={this.props.surveyRequest.surveyName} onChange={e => this.props.setSurveyRequestData({ surveyName: e.target.value })} className="form-control" disabled={!this.props.appServices.userContext?.isEditor} />
                                </div>
                            </div>
                        </div>}
                        {this.shouldDisplayCampaignSection() && <div className="group">
                            <div className="title">{this.localizeString("Campaign")}</div>
                            <div className="content">
                                {this.renderCampaignPicker()}
                            </div>
                        </div>}
                        <div className="group">
                            <div className="title">{this.localizeString("Material Identifiers")}</div>
                            <div className="content">
                                <div className="alert alert-info">
                                    {this.localizeString("Material Identifers are additional information added to each material to better identify the material to a supplier.")}
                                </div>
                                {Object.keys(SurveyStateController.additionalIdentityColumnsIds).map(id => <CheckBox key={id} isDisabled={!this.isSurveyEditable()} checked={this.props.surveyRequest.metadata.additionalColumnIdIdentifiers.includes(id)} checkboxLabelText={this.props.surveyMetadata.requestableColumnIdToFriendlyNameMap[id] ?? this.localizeString("Unknown")} onValueChange={c => this.additionalColumnIdIdentifersCheckbox_onChange(c, id)} />)}
                            </div>
                        </div>
                        {this.shouldDisplaySupplierAndMaterialData() &&
                        <div className="group">
                            <div className="title">
                                {tableProps.lockSuppliers ? this.localizeString("Supplier and Materials") : this.localizeString("Suppliers and Materials")}
                            </div>
                            <div className="content">
                                {this.isSurveyEditable() &&
                                    <div className="survey-suppliers-top-action-bar">
                                        {this.props.surveyUi.currentState === Models.SurveyMode.New &&
                                            <>
                                                <button type="button" className="btn btn-toxnot-primary" onClick={this.props.addSuppliers}><span className="glyphicon glyphicon-plus"></span>{" "}{this.localizeString("Add Supplier")}</button>
                                                <button type="button" className="btn btn-toxnot-danger" onClick={this.props.removeSuppliers}><span className="glyphicon glyphicon-remove"></span>{" "}{this.localizeString("Remove Supplier")}</button>
                                            </>
                                        }
                                        {this.props.appServices.userContext?.isEditor && <button type="button" onClick={e => $("#edit-custom-names-modal").modal("show")} className="btn btn-default">{this.localizeString("Edit Custom Names")}</button>}
                                        {" "}
                                    </div> 
                                    }
                                    {(this.props.surveyUi.currentState === Models.SurveyMode.EditExisting) && (this.props.surveyRequest.bomType === 1) && (this.props.surveyRequest.state !== 4)
                                        ? <p>{this.localizeString("No Materials Selected. Your supplier will choose the materials they respond with.")}</p>
                                        : (
                                            <SelectSuppliersMaterialsTable
                                                options={tableProps}
                                                initialInviteSupplerModel={this.props.surveyUi.currentState === Models.SurveyMode.New ? this.props.location.state?.supplierRowModels : this.convertResponseInventoryMetadataTableToInviteSupplierModel(this.props.surveyRequest.requestInventoryMetadataList)}
                                                onInviteSupplierModelDataChanged={supplierRowModels => this.selectSuppliersMaterialsTable_modelDataChanged(supplierRowModels)}
                                            />
                                        )
                                    }
                            </div>
                        </div>}
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private isSurveyEditable() {
        return ((this.props.surveyUi.currentState === Models.SurveyMode.New) || this.props.surveyRequest.state === 4 || this.props.surveyUi.currentState === Models.SurveyMode.Template) && this.props.appServices.userContext?.isEditor;
    }

    // ===============================================================================

    private getCurrentTemplateId() {
        return this.props.surveyUi.currentTemplateId ?? this.props.surveyRequest.surveyTemplateId;
    }

    // ===============================================================================

    private getCampaignsForCurrentTemplate() {
        const currentTemplateId = this.getCurrentTemplateId();

        return this.props.surveyMetadata.campaigns
            .filter(c => c.templateId === currentTemplateId)
            .orderBy(c => c.campaignName);
    }

    // ===============================================================================

    private renderCampaignPicker() {
        const availableCampaigns = this.getCampaignsForCurrentTemplate();
        const selectedCampaign =
            availableCampaigns.firstOrDefault(c => c.campaignId === this.props.surveyRequest.campaignId)
            ?? this.props.surveyMetadata.campaigns.firstOrDefault(c => c.campaignId === this.props.surveyRequest.campaignId);

        const buttonText =
            selectedCampaign?.campaignName
            ?? this.props.surveyRequest.campaignName ?? this.localizeString("Select Campaign");
        const footerStartIndex = availableCampaigns.length;

        return (
            <div className="btn-group">
                <button type="button" id="campaign-dropdown" className="btn btn-default btn-sm dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                    <span className="template-dropdown-button-text">{buttonText}</span>
                    <span className="caret"></span>
                </button>
                <ul className="dropdown-menu scrollable">
                    {availableCampaigns.map(campaign => (
                        <li key={campaign.campaignId}>
                            <a role="button" onClick={e => this.campaignPicker_campaignSelected(campaign.campaignId)}>
                                <div className="title">{campaign.campaignName}</div>
                            </a>
                        </li>
                    ))}
                    {availableCampaigns.length === 0 && (
                        <li className="no-campaigns-available" key="no-campaigns-available">
                            <span className="dropdown-menu-item-disabled">{this.localizeString("No campaigns available for this template.")}</span>
                        </li>
                    )}
                    <li key={footerStartIndex} role="separator" className="divider"></li>
                    <li key={footerStartIndex + 1}>
                        <Link role="button" to={CreateAndEditSurveyComponentPaths.SurveyCampaignsPath}>
                            <div className="title">{this.localizeString("Edit Campaigns...")}</div>
                        </Link>
                    </li>
                </ul>
            </div>
        );
    }

    // ===============================================================================

    private campaignPicker_campaignSelected = (campaignId: string | null) => {
        const selectedCampaign = this.props.surveyMetadata.campaigns.firstOrDefault(c => c.campaignId === campaignId);

        this.props.setSurveyRequestData({
            campaignId: campaignId,
            campaignName: selectedCampaign?.campaignName ?? null
        });
    }

    // ===============================================================================

    private convertResponseInventoryMetadataTableToInviteSupplierModel(metadataList: Models.SurveyRequestInventoryMetadata[]): InviteSupplierModel[] {
        
        const requestedMats = metadataList.map<InventoryOrCustomNameWithPossibleId>((metadata: Models.SurveyRequestInventoryMetadata, _): InventoryOrCustomNameWithPossibleId => { 
            return { 
                inventoryId: metadata.inventoryId, 
                name: metadata.originalName, 
                customName: metadata.originalName === metadata.customizedName ? null : metadata.customizedName
            } 
        });

        if (this.props.surveyRequest.supplierId === null) {
            throw new Error("This is considered an existing survey, and the supplier id therefore cannot be null.");
        }

        const supplierNameToUse = this.props.surveyRequest.sendToCompany ?? "";

        if (supplierNameToUse === "") {
            throw new Error("Unable to determine the company name.")
        }

        const model: InviteSupplierModel = {
            supplierId: this.props.surveyRequest.supplierId,
            supplierName: supplierNameToUse,
            contactEmail: this.props.surveyRequest.sendToEmail,
            requestedMaterials: requestedMats
        };

        return [model];
    }

    // ===============================================================================7

    private selectSuppliersMaterialsTable_modelDataChanged = (supplierRowModels: InviteSupplierModel[]) => {
        const requestedMaterials = supplierRowModels[0]?.requestedMaterials ?? [];
        const requestInventoryMetadataList = requestedMaterials
            .filter(material => material.inventoryId !== null)
            .map<Models.SurveyRequestInventoryMetadata>(material => ({
                inventoryId: material.inventoryId!,
                customizedName: material.customName ?? "",
                originalName: material.name
            }));

        this.props.setSurveyRequestData({
            requestInventoryMetadataList: requestInventoryMetadataList
        });

        this.props.fetchInventorySummaryMetadata(supplierRowModels);
    }

    // ===============================================================================7

    private additionalColumnIdIdentifersCheckbox_onChange = (checked: boolean, columnId: string) => {
        const newColumnIds = [...this.props.surveyRequest.metadata.additionalColumnIdIdentifiers.filter(id => id !== columnId)];

        if (checked) {
            newColumnIds.push(columnId);
        }

        this.props.setSurveyRequestData({
            metadata: {
                ...this.props.surveyRequest.metadata,
                additionalColumnIdIdentifiers: newColumnIds
            }
        });
    }
}

const mapDispatchToProps = { ...SurveyStateController.actionCreators, addSuppliers: SelectSuppliersStateControllers.actionCreators.addSupplierToInvite, removeSuppliers: SelectSuppliersStateControllers.actionCreators.removeSuppliersToInvite };

export default connect((state: ApplicationState) => ({ ...state.createAndEditSurveysState, appServices: state.appServices}),mapDispatchToProps)(withRouter(CreateAndEditSurveySummaryPage) as any);
